/**
 * 基于 Fetch API 的统一请求工具
 * 提供 GET、POST 等常用 HTTP 方法封装
 */

// ==================== 基础配置 ====================
const BASE_URL = process.env.REACT_APP_API_BASE_URL || ''; // 可通过环境变量配置

// 默认请求配置
const DEFAULT_OPTIONS = {
  timeout: 10000, // 默认超时时间 10s
  headers: {
    'Content-Type': 'application/json',
  },
};

// ==================== 错误处理 ====================
class RequestError extends Error {
  constructor(message, status, data) {
    super(message);
    this.name = 'RequestError';
    this.status = status;
    this.data = data;
  }
}

// ==================== 超时控制 ====================
const fetchWithTimeout = (url, options, timeout) => {
  return Promise.race([
    fetch(url, options),
    new Promise((_, reject) =>
      setTimeout(
        () => reject(new RequestError('请求超时', 408, null)),
        timeout,
      ),
    ),
  ]);
};

// ==================== 响应处理 ====================
const handleResponse = async (response) => {
  // const contentType = response.headers.get('content-type');

  // 解析响应数据
  let data;
  // if (contentType && contentType.includes('application/json')) {
  data = await response.json();

  // } else {
  //   data = await response.text();
  // }

  // 检查 HTTP 状态码
  if (!response.ok) {
    throw new RequestError(
      data.message || `HTTP error! status: ${response.status}`,
      response.status,
      data,
    );
  }

  return data?.data ? data?.data : data;
};

// ==================== 核心请求方法 ====================
const request = async (url, options = {}) => {
  try {
    // 合并配置
    const config = {
      ...DEFAULT_OPTIONS,
      ...options,
      headers: {
        ...DEFAULT_OPTIONS.headers,
        ...options.headers,
      },
    };

    // 构建完整 URL
    const fullUrl = url.startsWith('http') ? url : `${BASE_URL}${url}`;

    // 执行请求
    const response = await fetchWithTimeout(fullUrl, config, config.timeout);

    // 处理响应
    return await handleResponse(response);
  } catch (error) {
    // 统一错误处理
    if (error instanceof RequestError) {
      console.error(`[Request Error] ${error.message}`, {
        status: error.status,
        data: error.data,
      });
      throw error;
    }

    // 网络错误或其他异常
    console.error('[Network Error]', error.message);
    throw new RequestError('网络连接失败，请检查网络设置', 0, null);
  }
};

// ==================== 便捷方法 ====================
export const http = {
  /**
   * GET 请求
   * @param {string} url - 请求地址
   * @param {object} params - 查询参数
   * @param {object} options - 其他配置
   */
  get: (url, params = {}, options = {}) => {
    // 确保 params 是对象类型
    const safeParams = params && typeof params === 'object' ? params : {};
    const queryString = new URLSearchParams(safeParams).toString();
    const fullUrl = queryString ? `${url}?${queryString}` : url;
    return request(fullUrl, {
      method: 'GET',
      ...options,
    });
  },

  /**
   * POST 请求
   * @param {string} url - 请求地址
   * @param {object} data - 请求体数据
   * @param {object} options - 其他配置
   */
  post: (url, data = {}, options = {}) => {
    return request(url, {
      method: 'POST',
      body: JSON.stringify(data),
      ...options,
    });
  },
};

export default http;
